import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error";
import Modal from "@via/components/modal.tsx";
import Table, { TableSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import {
  ERROR,
  ModalSize,
  PROJECT_BUCKET_MEMBERS_TABLE,
} from "@via/utils/constants";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { ProjectBucketMember } from "common";
import { useState, type FC } from "react";
import AddBucketMember from "@via/pages/projects/settings/add-bucket-member.tsx";
import { useListProjectBucketMembers } from "@via/pages/projects/project-bucket.api.tsx";
import DeleteProjectBucketMemberModal from "@via/pages/projects/settings/delete-project-bucket-member-modal.tsx";

type ProjectBucketMemberModalProps = {
  projectId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const defaultParams = {
  page: 0,
  pageSize: 10,
};

const columnHelper = createColumnHelper<ProjectBucketMember>();

const ProjectBucketMemberModal: FC<ProjectBucketMemberModalProps> = ({
  projectId,
  open,
  setOpen,
}) => {
  const [page, setPage] = useState(defaultParams.page);
  const [pageSize, setPageSize] = useState(defaultParams.pageSize);
  const [memberToBeDeleted, setMemberToBeDeleted] =
    useState<ProjectBucketMember | null>(null);

  const { data, isLoading, isError } = useListProjectBucketMembers(
    projectId,
    page,
    pageSize
  );

  const handleAction = (
    option: TableContextMenuInterface,
    member: ProjectBucketMember
  ) => {
    switch (option.name) {
      case "Delete":
        setMemberToBeDeleted(member);
        break;
      default:
        break;
    }
  };

  const columns = [
    columnHelper.accessor("memberEmail", {
      cell: info => info.getValue(),
      header: () => <span>Email</span>,
    }),
    columnHelper.accessor("dateModified", {
      cell: info => info.getValue(),
      header: () => <span>Date Added</span>,
    }),
    columnHelper.accessor("id", {
      cell: info => (
        <TableContextMenu
          options={PROJECT_BUCKET_MEMBERS_TABLE}
          onSelect={option => handleAction(option, info.row.original)}
        />
      ),
      header: () => <span>Options</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ProjectBucketMember>>;

  return (
    <Modal
      size={ModalSize.Large}
      width={1000}
      open={open}
      setOpen={() => {
        setOpen(false);
      }}
      title="View Project Bucket Members">
      <AddBucketMember projectId={projectId} />
      {isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <>
          <Table
            isLoading={isLoading}
            loaderCount={6}
            data={data?.data || []}
            columns={columns}
            total={data?.total || 0}
            size={TableSize.Big}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </>
      )}
      {memberToBeDeleted && (
        <DeleteProjectBucketMemberModal
          projectId={projectId}
          member={memberToBeDeleted}
          setMemberToBeDeleted={setMemberToBeDeleted}
        />
      )}
    </Modal>
  );
};

export default ProjectBucketMemberModal;
